import React, { useState, useEffect, useCallback, useRef } from "react";
import constant from "../../constant";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import PartyForm from "../ui/partyform";
import ItemForm from "../ui/itemform";

// Tax Calculation Function (as provided)
const calculateTaxDetails = (items, isInterState) => {
  const taxDetails = {
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
  };

  items.forEach((item) => {

    const taxableAmount = parseFloat(item.itemAmount); // Ensure itemAmount is parsed as float
    const taxRate = parseFloat(item.taxRate); // Ensure taxRate is parsed as float
    let taxAmount, splitRate;

    if (isInterState) {
      // IGST calculation
      taxAmount = (taxableAmount * taxRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100; // Round to 2 decimal places
      taxDetails.igst[taxRate] = (taxDetails.igst[taxRate] || 0) + taxAmount;
    } else {
      // CGST/SGST calculation
      splitRate = taxRate / 2;
      taxAmount = (taxableAmount * splitRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100; // Round to 2 decimal places

      // Round CGST and SGST for the current splitRate
      taxDetails.cgst[splitRate] =
        Math.round(((taxDetails.cgst[splitRate] || 0) + taxAmount) * 100) / 100;
      taxDetails.sgst[splitRate] =
        Math.round(((taxDetails.sgst[splitRate] || 0) + taxAmount) * 100) / 100;
    }

    // Accumulate total tax and grand total
    taxDetails.totalTax += isInterState ? taxAmount : taxAmount * 2;
    taxDetails.grandTotal += parseFloat(item.totalAmount); // Ensure totalAmount is parsed as float
  });

  // Round totalTax and grandTotal to 2 decimal places
  taxDetails.totalTax = Math.round(taxDetails.totalTax * 100) / 100;
  taxDetails.grandTotal = Math.round(taxDetails.grandTotal * 100) / 100;

  return taxDetails;
};

const SaleForm = ({
  billTo,
  setBillTo,
  items,
  setItems,
  invoiceNo,
  setInvoiceNo,
  date,
  handleDateChange,
  paymentTerms,
  handlePaymentTermsChange,
  dueDate,
  notes,
  setNotes,
  termsAndConditions,
  setTermsAndConditions,
  additionalCharges,
  handleAddAdditionalCharge,
  handleAdditionalChargeChange,
  handleRemoveAdditionalCharge,
  handleAddItem,
  handleRemoveItem,
  discount,
  setDiscount,
  discountType,
  setDiscountType,
  amountReceived,
  setAmountReceived,
  paymentMode,
  setPaymentMode,
  fullyPaid,
  handleFullyPaidChange,
  error,
}) => {
  const [partyData, setPartyData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [localError, setLocalError] = useState(null);
  const navigate = useNavigate();
  const [lastInvoiceDate, setLastInvoiceDate] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingAnother, setIsAddingAnother] = useState(false);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [isAddPartyModalOpen, setIsAddPartyModalOpen] = useState(false);
  const [newlyCreatedParty, setNewlyCreatedParty] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // New States for Tax Calculation
  const [companyGst, setCompanyGst] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [isInterState, setIsInterState] = useState(false);
  const [taxDetails, setTaxDetails] = useState({
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
  });

  // State for new Item form in modal - MATCHING PARTY FORM PATTERN
  const [newItemName, setNewItemName] = useState("");
  const [newItemGstRate, setNewItemGstRate] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemType, setNewItemType] = useState("");
  const [newItemHsnCode, setNewItemHsnCode] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("");

  const menuPortalTarget = useRef(document.body);

  const fetchFormData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/sales/form-data`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || "Failed to fetch form data");
      }

      const data = await response.json();

      if (data.success) {
        setPartyData(data.data.partyData);
        setItemData(data.data.itemData);
        setInvoiceNo(data.data.invoiceNumber);
        setTermsAndConditions(data.data.terms);
        setCompanyGst(data.data.companyGst); // Set Company GST
        setCompanyState(data.data.companyState);
        setIsProfileComplete(data.data.isProfileComplete);
        setLastInvoiceDate(data.data.lastInvoiceDate);
      } else {
        alert(data.message || "Failed to fetch form data");
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData, newlyCreatedParty]);

  // useEffect(() => {
  //   if (!isProfileComplete) {
  //     navigate('/accounts/profile');
  //   }
  // }, [isProfileComplete]);

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...items];
    const currentItem = updatedItems[index];

    if (name === "item") {
      const selectedItem = itemData.find((item) => item._id === value.value);
      if (selectedItem) {
        let itemPrice = selectedItem.isTaxInclusive
          ? Math.round(
            (selectedItem.price /
              (1 + parseInt(selectedItem.gstRate) / 100)) *
            100
          ) / 100
          : Math.round(selectedItem.price * 100) / 100;
        updatedItems[index] = {
          ...updatedItems[index],
          [name]: value.value,

          hsnCode: selectedItem.hsnCode || "",
          unit: selectedItem.unit || "",
          taxRate:
            selectedItem.gstRate !== undefined
              ? selectedItem.gstRate.toString()
              : "",
          price: itemPrice || "",
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [name]: value.value,
          hsnCode: "",
          unit: "",
          taxRate: "",
          price: "",
        };
      }
    } else {
      updatedItems[index] = { ...updatedItems[index], [name]: value };
    }
    if (name === "item" && value && value.value === "add_new_item") {
      setIsAddItemModalOpen(true);
      updatedItems[index] = { ...currentItem, item: "" };
      setItems(updatedItems);
      return;
    }

    setItems(updatedItems);
  };

  // useEffect(() => {
  //   const updatedItemsForTax = items.map(item => {
  //     const selectedItemData = itemData.find(i => i._id === item.item);
  //     return {
  //       ...item,
  //       itemAmount: (parseFloat(item.price || 0) * parseInt(item.qty || 0) * (1 - parseFloat(item.discount || 0) / 100)).toFixed(2), // Calculate itemAmount for tax calculation
  //       taxRate: selectedItemData?.gstRate || 0 // Use item's taxRate
  //     };
  //   });
  //   setTaxDetails(calculateTaxDetails(updatedItemsForTax, isInterState));
  // }, [items, isInterState, itemData]);

  useEffect(() => {
    const updatedItemsForTax = items.map((item) => {
      const selectedItemData = itemData.find((i) => i._id === item.item);
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discount = parseFloat(item.discount || 0);
      const discountType = item.discountType || "%";

      let itemAmountBeforeDiscount = price * qty;
      let discountAmountPerUnit = 0;

      if (discountType === "rs") {
        discountAmountPerUnit = discount; // Fixed discount per unit
      } else {
        discountAmountPerUnit = price * (discount / 100); // Percentage discount per unit
      }

      const totalDiscountAmount = discountAmountPerUnit * qty;
      const calculatedItemAmount =
        itemAmountBeforeDiscount - totalDiscountAmount;

      return {
        ...item,
        itemAmount: calculatedItemAmount.toFixed(2),
        price: price,
        taxRate: selectedItemData?.gstRate || 0,
        totalAmount: (
          calculatedItemAmount +
          (calculatedItemAmount * (selectedItemData?.gstRate || 0)) / 100
        ).toFixed(2), // Include tax in totalAmount
      };
    });
    setTaxDetails(calculateTaxDetails(updatedItemsForTax, isInterState));
  }, [items, isInterState, itemData]);

  useEffect(() => {
    const getIsInterStateVal = () => {
      if (billTo && billTo.billingAddress.gstNo && companyGst) {
        const partyGstPrefix = billTo.billingAddress.gstNo
          .substring(0, 2)
          .toUpperCase();
        const companyGstPrefix = companyGst.substring(0, 2).toUpperCase();
        setIsInterState(partyGstPrefix !== companyGstPrefix)

      } else {

        setIsInterState(companyState.toLowerCase() !== billTo?.billingAddress?.state.toLowerCase())


        // setIsInterState(false); // Default to intrastate if GST info is missing
      }
    };
    getIsInterStateVal();
  }, [billTo, companyGst]);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const partyOptions = [
    { value: "add_new_party", label: "+ Add New Party" },
    ...partyData.map((party) => ({
      value: party,
      label: party.name,
    })),
  ];

  const itemOptions = [
    { value: "add_new_item", label: "+ Add New Item" },
    ...itemData.map((item) => ({
      value: item._id,
      label: item.name,
    })),
  ];

  const handleToProfile = () => {
    navigate("/accounts/profile");
  };

  const handleBillToChange = (selectedOption) => {
    if (selectedOption && selectedOption.value === "add_new_party") {
      setIsAddPartyModalOpen(true);
      setBillTo(null);
      setSelectedBillingAddress(null);
      setSelectedShippingAddress(null);
    } else {
      setBillTo(selectedOption ? selectedOption.value : null);
      setSelectedBillingAddress(
        selectedOption && selectedOption.value
          ? selectedOption.value.billingAddress
          : null
      );
      setSelectedShippingAddress(
        selectedOption && selectedOption.value
          ? selectedOption.value.shippingAddress
          : null
      );
    }
  };

  const handleClosePartyModal = () => {
    setIsAddPartyModalOpen(false);
  };

  const handleCloseItemModal = () => {
    setIsAddItemModalOpen(false);
    setNewItemName("");
    setNewItemGstRate("");
    setNewItemPrice("");
    setNewItemType("");
    setNewItemHsnCode("");
    setNewItemUnit("");
    setLocalError(null);
  };

  const handlePartyFormSubmit = async (partyData) => {
    setIsAdding(true);
    setLocalError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLocalError("No authentication token found");
        return;
      }

      const response = await fetch(`${constant.REACT_APP_API_URL}/party`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(partyData),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to add party");
      }

      setIsAddPartyModalOpen(false);
      setNewlyCreatedParty(result.data);
      fetchFormData();
      setBillTo(result.data);
      setSelectedBillingAddress(result.data.billingAddress);
      setSelectedShippingAddress(result.data.shippingAddress);

      setLocalError(null);
    } catch (error) {
      console.error("Error adding party:", error);
      setLocalError(error.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleItemFormSubmit = async (itemData) => {
    setIsAdding(true);
    setLocalError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLocalError("No authentication token found");
        return;
      }

      const response = await fetch(`${constant.REACT_APP_API_URL}/item`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itemData),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to add item");
      }

      setIsAddItemModalOpen(false);
      fetchFormData();
      setNewItemName("");
      setNewItemGstRate("");
      setNewItemPrice("");
      setNewItemType("");
      setNewItemHsnCode("");
      setNewItemUnit("");
      setLocalError(null);
    } catch (error) {
      console.error("Error adding item:", error);
      setLocalError(error.message);
    } finally {
      setIsAdding(false);
    }
  };

  const resetForm = () => {
    setItems([
      {
        item: "",
        qty: 1,
        price: "",
        discount: "",
        discountType: "%",
        hsnCode: "",
        unit: "",
        taxRate: "",
        description: "",
      },
    ]);
    setNotes("");
    setTermsAndConditions("");
    setDiscount("");
    setDiscountType("%");
    setAmountReceived("");
    setPaymentMode("");
    setLocalError(null);
    setSuccessMessage("Sale added successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleAddAnotherSale = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setIsAddingAnother(true);
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLocalError("No authentication token found");
        return;
      }

      if (!billTo) {
        setLocalError("Please select a party to bill.");
        setIsAdding(false);
        setIsAddingAnother(false);
        return;
      }

      const itemsForApi = prepareItemsForApi();
      const payload = preparePayload(itemsForApi);
      payload.isInterstate = isInterState;


      const response = await fetch(`${constant.REACT_APP_API_URL}/sales`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add sale");
      }

      resetForm();
      fetchFormData();
    } catch (apiError) {
      console.error("Error adding sale (Add Another):", apiError);
      setLocalError(apiError.message);
    } finally {
      setIsAdding(false);
      setIsAddingAnother(false);
    }
  };

  const handleAddSale = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setIsAdding(true);
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLocalError("No authentication token found");
        return;
      }

      if (!billTo) {
        setLocalError("Please select a party to bill.");
        setIsAdding(false);
        return;
      }

      const itemsForApi = prepareItemsForApi();
      const payload = preparePayload(itemsForApi);
          payload.isInterstate = isInterState;
     

      const response = await fetch(`${constant.REACT_APP_API_URL}/sales`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add sale");
      }

      window.open(`/invoice/${result.data._id}`, "_blank");
      navigate("/sales");
    } catch (apiError) {
      console.error("Error adding sale (Add Sale):", apiError);
      setLocalError(apiError.message);
    } finally {
      setIsAdding(false);
    }
  };

  const prepareItemsForApi = () => {
    return items.map((item) => {
      const selectedItemData = itemData.find((i) => i._id === item.item);
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discount = parseFloat(item.discount || 0);
      const discountType = item.discountType || "%";
      const taxRate = parseFloat(item.taxRate || 0);

      let itemDiscountAmount = 0;
      if (discountType === "%") {
        itemDiscountAmount = price * qty * (discount / 100);
      } else if (discountType === "rs") {
        itemDiscountAmount = discount;
      }

      // const itemTaxableAmount = (price - itemDiscountAmount) * qty;
      const itemTaxableAmount = price * qty - itemDiscountAmount;
      const itemGstAmount = itemTaxableAmount * (taxRate / 100);
      const itemAmount = itemTaxableAmount + itemGstAmount;

      return {
        itemId: item.item,
        price: price,

        itemName: selectedItemData?.name || "",
        quantity: parseInt(item.qty || 0),
        discount: parseFloat(item.discount || 0),
        discountType: item.discountType || "%",
        itemAmount: itemTaxableAmount.toFixed(2), // taxable amount
        taxRate: parseFloat(item.taxRate || 0),
        hsnCode: selectedItemData?.hsnCode || "", // added hsn
        unit: selectedItemData?.unit || "", // added unit
        description: item.description || "", // added description
        taxAmount: itemGstAmount.toFixed(2), // added taxAmount
        totalAmount: itemAmount.toFixed(2), // total amount including tax
      };
    });
  };

  const preparePayload = (itemsForApi) => {
    const dueDateFormatted = new Date(dueDate)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");

    const calculatedSubtotals = calculateSubtotal();
    const totalAmountNoDecimal = parseFloat(
      calculatedSubtotals.totalAmountNoDecimal
    );
    const roundoffValue =
      parseFloat(calculatedSubtotals.totalAmount) - totalAmountNoDecimal;

    // Determine amountReceived and pendingAmount based on fullyPaid and user input
    let finalAmountReceived = 0;
    let finalPendingAmount = totalAmountNoDecimal;

    if (fullyPaid) {
      finalAmountReceived = totalAmountNoDecimal; // Set amountReceived to total amount (rounded)
      finalPendingAmount = 0; // No pending amount if fully paid
    } else if (amountReceived && parseFloat(amountReceived) > 0) {
      finalAmountReceived = parseFloat(amountReceived); // Use user-entered amount
      finalPendingAmount = totalAmountNoDecimal - finalAmountReceived; // Calculate pending amount
    }

    return {
      partyId: billTo._id,
      partyData: {
        name: billTo.name,
        mobile: billTo.mobile,
        billingAddress: billTo.billingAddress,
        shippingAddress: billTo.shippingAddress,
      },
      items: itemsForApi,
      invoiceDate: new Date(date),
      dueDate: dueDateFormatted,
      notes: notes || "",
      terms: termsAndConditions || "",
      invoiceNumber: invoiceNo,
      amountReceived: finalAmountReceived.toFixed(2), // Ensure 2 decimal places
      paymentMode: paymentMode || "",
      fullyPaid: fullyPaid,
      additionalCharges: additionalCharges.map((charge) => ({
        label: charge.label,
        value: parseFloat(charge.value || 0),
      })),
      additionalDiscount: parseFloat(discount || 0),
      additionalDiscountType: discountType || "%",
      totalAmount: parseFloat(calculatedSubtotals.totalAmount).toFixed(2),
      roundoff: roundoffValue.toFixed(2),
      pendingAmount: finalPendingAmount.toFixed(2), // Ensure 2 decimal places
      taxAmount: parseFloat(taxDetails.totalTax).toFixed(2),
    };
  };

  const handleAmountReceivedChange = (e) => {
    const value = e.target.value;
    if (
      value &&
      parseFloat(value) > parseFloat(subtotals.totalAmountNoDecimal)
    ) {
      setLocalError("Amount received cannot exceed total amount.");
      return;
    }
    setAmountReceived(value);
    setLocalError(null);
  };

  const calculateSubtotal = () => {
    let grossItemTotal = 0;
    let qtyTotal = 0;
    let totalDiscount = 0;
    let totalTaxableAmount = 0;
    let totalGstAmount = 0;
    let netItemTotal = 0;
    let preGlobalDiscountTotal = 0;
    let globalDiscountAmount = 0;
    let finalTotalAmount = 0;

    items.forEach((item) => {
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discount = parseFloat(item.discount || 0);
      const discountType = item.discountType || "%";
      const taxRate = parseFloat(item.taxRate || 0);

      grossItemTotal += price * qty;

      let itemDiscountAmountPerUnit = 0;
      if (discountType === "%") {
        itemDiscountAmountPerUnit = price * (discount / 100); // Discount per unit
      } else if (discountType === "rs") {
        itemDiscountAmountPerUnit = discount; // Fixed discount per unit
      }
      const totalItemDiscount = itemDiscountAmountPerUnit * qty; // Scale by quantity
      totalDiscount += totalItemDiscount;

      let itemTaxableAmount = (price - itemDiscountAmountPerUnit) * qty; // Taxable amount after discount
      totalTaxableAmount += itemTaxableAmount;

      let itemGstAmount = itemTaxableAmount * (taxRate / 100); // GST on taxable amount
      totalGstAmount += itemGstAmount;
      qtyTotal += qty;
    });

    netItemTotal = totalTaxableAmount + totalGstAmount;

    let additionalChargesSum = 0;
    additionalCharges.forEach((charge) => {
      additionalChargesSum += parseFloat(charge.value || 0);
    });

    preGlobalDiscountTotal = netItemTotal + additionalChargesSum;

    if (discountType === "%") {
      globalDiscountAmount =
        preGlobalDiscountTotal * (parseFloat(discount || 0) / 100);
    } else if (discountType === "rs") {
      globalDiscountAmount = parseFloat(discount || 0);
    }
    finalTotalAmount = preGlobalDiscountTotal - globalDiscountAmount;

    return {
      grossItemTotal: grossItemTotal.toFixed(2),
      qtyTotal: qtyTotal.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      totalTaxableAmount: totalTaxableAmount.toFixed(2),
      totalGstAmount: totalGstAmount.toFixed(2),
      netItemTotal: netItemTotal.toFixed(2),
      globalDiscount: globalDiscountAmount.toFixed(2),
      totalAmount: finalTotalAmount.toFixed(2),
      totalAmountNoDecimal: Math.round(finalTotalAmount).toFixed(0),
    };
  };

  const subtotals = calculateSubtotal();
  const balanceAmount = fullyPaid
    ? "0.00"
    : (
      parseFloat(subtotals.totalAmountNoDecimal) -
      parseFloat(amountReceived || 0)
    ).toFixed(2);

  return (
    <div>
      {!isProfileComplete ? (
        <div className="flex items-center justify-center p-6">
          <div className="max-w-sm w-full bg-white rounded-lg shadow-lg p-6 space-y-6">
            {/* Warning Icon */}
            <div className="flex items-center justify-center space-x-3">
              <div className="text-yellow-500 bg-yellow-100 p-3 rounded-full">
                <AlertTriangle className="w-8 h-8" />{" "}
                {/* Lucide warning icon */}
              </div>
            </div>

            {/* Warning Message */}
            <p className="text-lg flex justify-center text-gray-800">
              Your profile is incomplete.
            </p>

            {/* Action Button */}
            <button
              onClick={handleToProfile}
              className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 transition-all"
            >
              Go to Profile
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleAddSale}
          className="p-6 bg-white shadow-md rounded-md"
        >
          {localError && <p className="text-red-500 mb-4">{localError}</p>}
          {successMessage && (
            <p className="text-green-500 mb-4">{successMessage}</p>
          )}

          {/* {!loading && !companyGst && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 my-4 rounded">
            <p className="font-semibold">Complete Your Profile</p>
            <p>Please provide your company GST details to continue.</p>
            <button
              type="button"
              onClick={() => navigate('/accounts/profile')}
              className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
            >
              Update Profile
            </button>
          </div>
        )} */}

          <div className="flex flex-col md:flex-row items-start justify-between w-full mb-6">
            {/* Bill To */}
            <div className="w-full md:w-3/4 md:pr-4 mb-4 md:mb-0">
              <label
                htmlFor="billTo"
                className="block text-gray-700 text-sm font-bold mb-2"
              >
                Bill To
              </label>
              <Select
                id="billTo"
                options={partyOptions}
                value={
                  billTo
                    ? partyOptions.find(
                      (option) =>
                        option.value && option.value._id === billTo._id
                    )
                    : null
                }
                onChange={handleBillToChange}
                className="shadow-md text-sm"
                classNamePrefix="react-select"
                placeholder="Search Party or Add New"
                required
                classNameContainer="w-full"
                menuPortalTarget={menuPortalTarget.current}
              />

              <div className="flex flex-col md:flex-row justify-between gap-2 mt-2">
                {/* Display Billing Address */}
                {selectedBillingAddress && (
                  <div className="mt-2 p-3 border rounded text-sm bg-gray-50 w-full">
                    <h1 className="font-semibold mb-1">Billing Address</h1>
                    <p>{selectedBillingAddress.name}</p>
                    <p>{selectedBillingAddress.streetAddress}</p>
                    <p>{`${selectedBillingAddress.city}, ${selectedBillingAddress.state}, ${selectedBillingAddress.country} - ${selectedBillingAddress.pincode}`}</p>
                  </div>
                )}
                {selectedShippingAddress && (
                  <div className="mt-2 p-2 border rounded text-sm bg-gray-50 w-full">
                    <h1 className="font-semibold mb-1">Shipping Address</h1>
                    <p>{selectedShippingAddress.name}</p>
                    <p>{selectedShippingAddress.streetAddress}</p>
                    <p>{`${selectedShippingAddress.city}, ${selectedShippingAddress.state}, ${selectedShippingAddress.country} - ${selectedShippingAddress.pincode}`}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="w-full md:w-1/3">
              {" "}
              {/* Take full width on small, and 1/3 on medium+ */}
              <div className="flex flex-col space-y-2 md:space-y-0 md:space-x-4 md:flex-row md:items-center mb-4">
                {/* Invoice No */}
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="invoiceNo"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Invoice No
                  </label>
                  <input
                    type="text"
                    id="invoiceNo"
                    placeholder="your invoice number"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    className="shadow appearance-none text-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                    disabled
                  />
                </div>

                {/* Date */}
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="date"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                  min={
                      lastInvoiceDate
                        ? new Date(lastInvoiceDate).toISOString().slice(0, 10)
                        : "2025-04-01"
                    }
                    value={date ? new Date(date).toISOString().slice(0, 10) : ""}
                    onChange={handleDateChange}
                    className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col space-y-2 md:space-y-0 md:space-x-4 md:flex-row md:items-center">
                {/* Payment Terms */}
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="paymentTerms"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Payment terms
                  </label>
                  <input
                    type="number"
                    id="paymentTerms"
                    value={paymentTerms}
                    placeholder="in days"
                    onChange={handlePaymentTermsChange}
                    className="text-sm shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>

                {/*  Due Date */}
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="dueDate"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    value={dueDate}
                    className="shadow appearance-none text-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Items - Table Format */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Item(s)
            </label>
            <div className="overflow-x-auto">
              <table className="min-w-full w-[90vw] table-auto border-collapse border border-gray-200 shadow-md text-xs md:text-sm">
                {" "}
                {/* Reduced text size on smaller screens */}
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[20%]">
                      Item
                    </th>
                    {/* <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[15%]">Description</th> */}
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      HSN/SCN
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      QTY
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Unit
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      MRP
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Price
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[10%]">
                      Discount
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Taxable Amount
                    </th>

                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      GST(%)
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Amount
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-center text-xs font-bold text-gray-700 w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[22%]">
                        <Select
                          id={`item-${index}`}
                          name="item"
                          options={itemOptions}
                          value={
                            itemOptions.find(
                              (option) => option.value === item.item
                            ) || null
                          }
                          onChange={(value) =>
                            handleItemChange(index, {
                              target: { name: "item", value },
                            })
                          }
                          className="shadow-md text-xs"
                          classNamePrefix="react-select"
                          placeholder="Search Item"
                          required
                          menuPortalTarget={menuPortalTarget.current}
                        />
                        <input
                          type="text"
                          id={`description-${index}`}
                          name="description"
                          value={item.description || ""}
                          onChange={(e) => handleItemChange(index, e)}
                          placeholder="Enter description"
                          className="shadow mt-1 appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                        />
                      </td>
                      {/* <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[15%]"
                    >
                      <input
                        type="text"
                        id={`description-${index}`}
                        name="description"
                        value={item.description || ""}
                        onChange={(e) => handleItemChange(index, e)}
                        placeholder="Enter description"
                        className="shadow appearance-none border rounded w-full py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                      />
                    </td> */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`hsn-${index}`}
                          name="hsnCode"
                          value={item.hsnCode}
                          onChange={(e) => handleItemChange(index, e)}
                          disabled
                          className="shadow w-[80px] appearance-none border rounded  py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[5%]">
                        <input
                          type="number"
                          id={`qty-${index}`}
                          name="qty"
                          value={item.qty}
                          onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[75px] appearance-none border rounded  py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                          required
                          min={0}
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`unit-${index}`}
                          name="unit"
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, e)}
                          disabled
                          className="shadow w-[75px] appearance-none border rounded  py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[5%]">
                        <input
                          type="text"
                          id={`mrp-${index}`}
                          name="mrp"
                          value={Math.round(
                            parseInt(item.price) *
                            parseFloat(1 + parseInt(item.taxRate) / 100)
                          )}
                          // onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[60px] appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                          disabled
                        // min={0}
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[5%]">
                        <input
                          type="number"
                          id={`price-${index}`}
                          name="price"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[100px] appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                          required
                        // min={0}
                        />
                      </td>
                      <td className="border border-gray-200 px-0 py-2 md:px-0 text-xs">
                        <div className="flex mx-2 w-[140px]">
                          <input
                            type="number"
                            id={`discount-${index}`}
                            name="discount"
                            value={item.discount}
                            onChange={(e) => handleItemChange(index, e)}
                            min={0}
                            className="shadow appearance-none border rounded w-3/5 py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                          />
                          <select
                            className="shadow border rounded w-2/5 py-1 px-2 md:py-2 md:px-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                            name="discountType"
                            value={item.discountType}
                            onChange={(e) => handleItemChange(index, e)}
                          >
                            <option>%</option>
                            <option>rs</option>
                          </select>
                        </div>
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs">
                        <input
                          type="text"
                          id={`taxableAmount-${index}`}
                          name="taxableAmount"
                          value={(() => {
                            const priceCalc = parseFloat(item.price || 0);
                            const qtyCalc = parseInt(item.qty || 0);
                            const discountCalc = parseFloat(item.discount || 0);
                            const discountTypeCalc = item.discountType || "%";

                            let itemDiscountAmountPerUnitCalc = 0;
                            if (discountTypeCalc === "%") {
                              itemDiscountAmountPerUnitCalc =
                                priceCalc * (discountCalc / 100); // Discount per unit
                            } else if (discountTypeCalc === "rs") {
                              itemDiscountAmountPerUnitCalc = discountCalc; // Fixed discount per unit
                            }

                            const taxableAmountCalc =
                              (priceCalc - itemDiscountAmountPerUnitCalc) *
                              qtyCalc; // Taxable amount after discount
                            return taxableAmountCalc.toFixed(2);
                          })()}
                          disabled
                          className="w-[100px] shadow appearance-none border rounded  py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                        />
                      </td>

                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`taxRate-${index}`}
                          name="taxRate"
                          value={item.taxRate}
                          onChange={(e) => handleItemChange(index, e)}
                          disabled
                          className="shadow w-[50px] appearance-none border rounded  py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                        />
                      </td>

                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[10%]">
                        <input
                          type="text"
                          id={`amount-${index}`}
                          name="amount"
                          value={(() => {
                            const priceCalc = parseFloat(item.price || 0);
                            const qtyCalc = parseInt(item.qty || 0);
                            const discountCalc = parseFloat(item.discount || 0);
                            const discountTypeCalc = item.discountType || "%";
                            const taxRateCalc = parseFloat(item.taxRate || 0);

                            let itemDiscountAmountPerUnitCalc = 0;
                            if (discountTypeCalc === "%") {
                              itemDiscountAmountPerUnitCalc =
                                priceCalc * (discountCalc / 100); // Discount per unit
                            } else if (discountTypeCalc === "rs") {
                              itemDiscountAmountPerUnitCalc = discountCalc; // Fixed discount per unit
                            }

                            const totalItemDiscountCalc =
                              itemDiscountAmountPerUnitCalc * qtyCalc; // Scale by quantity
                            const taxableAmountCalc =
                              (priceCalc - itemDiscountAmountPerUnitCalc) *
                              qtyCalc; // Taxable amount after discount
                            const gstAmountCalc =
                              taxableAmountCalc * (taxRateCalc / 100); // GST on taxable amount
                            const finalAmountCalc =
                              taxableAmountCalc + gstAmountCalc;

                            return finalAmountCalc.toFixed(2);
                          })()}
                          disabled
                          className="shadow w-[100px] appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-center">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-gray-50">
                    <td
                      colSpan="2"
                      className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs"
                    >
                      Items Total
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs">
                      {subtotals.qtyTotal}
                    </td>
                    <td></td>
                    <td></td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs">
                      {/* {subtotals.grossItemTotal} */}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs">
                      {subtotals.totalDiscount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs">
                      {subtotals.totalTaxableAmount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-4 text-left text-xs">
                      {subtotals.totalGstAmount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-4 text-left text-xs">
                      {subtotals.netItemTotal}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Add Item Button with Icon */}
            <button
              type="button"
              onClick={handleAddItem}
              className="flex items-center gap-2 text-sm text-black font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mt-2"
            >
              <Plus size={16} /> Add Item
            </button>
          </div>

          {/* Additional Charges */}
          {/* <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">Additional Charges <span className='text-xs'>(optional)</span> </label>
          {additionalCharges.map((charge, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                name="label"
                value={charge.label}
                onChange={(e) => handleAdditionalChargeChange(index, e)}
                placeholder="Label"
                className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2 text-xs"
              />
              <input
                type="number"
                name="value"
                value={charge.value}
                onChange={(e) => handleAdditionalChargeChange(index, e)}
                placeholder="Value"
                className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
              />
              <button type="button" onClick={() => handleRemoveAdditionalCharge(index)} className="text-red-600 hover:text-red-800 px-4">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddAdditionalCharge} className="text-sm text-black font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
            + Add Charge
          </button>
        </div> */}

          <div className="flex flex-col md:flex-row justify-between gap-4">
            {" "}
            {/* Responsive flex container for Notes/Terms and Summary */}
            <div className="w-full md:w-1/2">
              {" "}
              {/* Take full width on small, and 1/2 on medium+ */}
              {/* Notes */}
              <div className="mb-4">
                <label
                  htmlFor="notes"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Notes <span className="text-xs">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  placeholder="enter notes(if any)"
                  onChange={(e) => setNotes(e.target.value)}
                  rows="5"
                  className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              {/* Terms and Conditions */}
              <div className="mb-4">
                <label
                  htmlFor="termsAndConditions"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Terms and Conditions{" "}
                  <span className="text-xs">(optional)</span>
                </label>
                <textarea
                  id="termsAndConditions"
                  value={termsAndConditions}
                  placeholder="your t&c"
                  rows="5"
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                  className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2">
              {" "}
              {/* Take full width on small, and 1/2 on medium+ */}
              {/* 
            <div className="mb-4">
              <label htmlFor="discount" className="block text-gray-700 text-sm font-bold mb-2">
                Discount <span className='text-xs'>(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  id="discount"
                  placeholder='enter discount'
                  value={discount}
                  min={0}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="shadow text-sm appearance-none border rounded w-2/3 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
                <select
                  className="shadow border rounded w-1/3 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                >
                  <option>%</option>
                  <option>rs</option>
                </select>
              </div>
            </div> */}
              {/* Mark Fully Paid */}
              {/* <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="fullyPaid"
                  checked={fullyPaid}
                  onChange={handleFullyPaidChange}
                  className="mr-2 leading-tight"
                />
                <label
                  htmlFor="fullyPaid"
                  className="text-gray-700 text-sm font-bold"
                >
                  Mark fully Paid
                </label>
              </div> */}
              <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                {" "}
                {/* Responsive flex for Amount Received and Payment Mode */}
                {/* <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="amountReceived"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Amount Received
                  </label>
                  <input
                    type="number"
                    id="amountReceived"
                    placeholder="enter received amount"
                    value={
                      fullyPaid
                        ? subtotals.totalAmountNoDecimal
                        : amountReceived
                    }
              
                    onChange={handleAmountReceivedChange}
                    className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    min={0}
                    disabled={fullyPaid}
                  />
                </div> */}
                {/* <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="paymentMode"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Payment Mode
                  </label>
                  <select
                    id="paymentMode"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="shadow border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="" disabled>
                      Select Payment Mode
                    </option>
                 
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="bank transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank</option>
                    <option value="other">Other</option>
                  </select>
                </div> */}
              </div>
              <div className="my-4">
                <div className="flex justify-start mb-2">
                  <div className="w-full px-2">
                    <div className="mb-4">
                      {/* <div className="flex justify-between">
                      <label className="block text-gray-700 text-sm font-bold mb-2">Items Total:</label>
                      <span className="text-sm font-semibold">{subtotals.grossItemTotal}</span>
                    </div> */}

                      <div className="flex justify-between">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Taxable Amount:
                        </label>
                        <span className="text-sm">
                          {subtotals.totalTaxableAmount}
                        </span>
                      </div>

                      <h3 className="text-sm font-bold mb-2">
                        Tax Summary (
                        {isInterState ? "Inter-State" : "Intra-State"})
                      </h3>

                      {/* 1. Collect all unique tax rates, sort, and then map */}
                      {(() => {
                        const allRates = new Set([
                          ...Object.keys(taxDetails.cgst),
                          ...Object.keys(taxDetails.sgst),
                          ...Object.keys(taxDetails.igst),
                        ]);
                        const sortedRates = Array.from(allRates).sort(
                          (a, b) => parseFloat(b) - parseFloat(a)
                        );

                        return sortedRates.map((rate) => (
                          <React.Fragment key={`rate-group-${rate}`}>
                            {taxDetails.cgst[rate] > 0 && (
                              <div
                                key={`cgst-${rate}`}
                                className="flex justify-between text-sm"
                              >
                                <span>CGST @ {rate}%:</span>
                                <span>{taxDetails.cgst[rate].toFixed(2)}</span>
                              </div>
                            )}
                            {taxDetails.sgst[rate] > 0 && (
                              <div
                                key={`sgst-${rate}`}
                                className="flex justify-between text-sm"
                              >
                                <span>SGST @ {rate}%:</span>
                                <span>{taxDetails.sgst[rate].toFixed(2)}</span>
                              </div>
                            )}
                            {taxDetails.igst[rate] > 0 && (
                              <div
                                key={`igst-${rate}`}
                                className="flex justify-between text-sm"
                              >
                                <span>IGST @ {rate}%:</span>
                                <span>{taxDetails.igst[rate].toFixed(2)}</span>
                              </div>
                            )}
                          </React.Fragment>
                        ));
                      })()}

                      <div className="flex justify-between font-semibold text-sm mt-2">
                        <span>Total Tax:</span>
                        <span>{taxDetails.totalTax.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* <div className="flex justify-between">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Subtotal:</label>
                    <span className="text-sm">{subtotals.netItemTotal}</span>
                  </div> */}
                    {/* <div className="flex justify-between">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Global Discount:</label>
                    <span className="text-sm">{subtotals.globalDiscount}</span>
                  </div> */}
                    <div className="flex justify-between">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Total Amount:
                      </label>
                      <span className="text-sm">{subtotals.totalAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Total Amount:(roundoff)
                      </label>
                      <span className="text-sm font-semibold">
                        {" "}
                        {subtotals.totalAmountNoDecimal}{" "}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Balance Amount:
                      </label>
                      <span className="text-sm font-semibold">
                        {" "}
                        {balanceAmount}{" "}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline text-sm" // Reduced font size on button
              disabled={isAddingAnother}
              onClick={handleAddAnotherSale}
            >
              {isAddingAnother ? "Adding..." : "Add Another"}
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline text-sm" // Reduced font size on button
              disabled={isAdding}
            >
              {isAdding ? "Adding..." : "Add Sale"}
            </button>
          </div>

          {/* Add Party Modal */}
          {isAddPartyModalOpen && (
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
              onClick={handleClosePartyModal}
            >
              <div
                className="relative top-20 mx-auto p-5 border w-full md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={handleClosePartyModal}
                >
                  ×
                </button>
                <h3 className="text-lg font-bold mb-4">Add New Party</h3>
                {localError && (
                  <p className="text-red-500 mb-4">{localError}</p>
                )}
                <PartyForm
                  onSubmit={handlePartyFormSubmit}
                  onCancel={handleClosePartyModal}
                />
              </div>
            </div>
          )}

          {/* Add Item Modal */}
          {isAddItemModalOpen && (
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
              onClick={handleCloseItemModal}
            >
              <div
                className="relative top-20 mx-auto p-5 border w-full md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={handleCloseItemModal}
                >
                  ×
                </button>

                <h3 className="text-lg font-bold mb-4">Add New Item</h3>
                {localError && (
                  <p className="text-red-500 mb-4">{localError}</p>
                )}
                <ItemForm
                  name={newItemName}
                  setName={setNewItemName}
                  gstRate={newItemGstRate}
                  setGstRate={setNewItemGstRate}
                  price={newItemPrice}
                  setPrice={setNewItemPrice}
                  itemType={newItemType}
                  setItemType={setNewItemType}
                  hsnCode={newItemHsnCode}
                  setHsnCode={setNewItemHsnCode}
                  unit={newItemUnit}
                  setUnit={setNewItemUnit}
                  loading={isAdding}
                  isEditing={false}
                  onSubmit={handleItemFormSubmit}
                  onCancel={handleCloseItemModal}
                  error={localError}
                />
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default SaleForm;
